import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Users, ShieldCheck, ShoppingBag, BarChart3, Trash2, ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import BrandMark from '@/components/BrandMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [freeAccessOnly, setFreeAccessOnly] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [familyThreads, setFamilyThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyDraft, setReplyDraft] = useState('');
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    const [s, u, p, m, t] = await Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeaders }),
      axios.get(`${API}/admin/users`, { headers: authHeaders }),
      axios.get(`${API}/admin/purchases`, { headers: authHeaders }),
      axios.get(`${API}/admin/messages`, { headers: authHeaders }),
      axios.get(`${API}/admin/messages/threads`, { headers: authHeaders }),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setPurchases(p.data);
    setMessages(m.data);
    setFamilyThreads(t.data);
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!user?.is_admin) return <Navigate to="/dashboard" />;
  if (loading) return null;

  const toggleFreeAccess = async (u, enabled) => {
    await axios.patch(`${API}/admin/users/${u.id}/free-access`, { enabled }, { headers: authHeaders });
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, free_access: enabled } : x));
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} and all their data? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeaders });
    setUsers(prev => prev.filter(x => x.id !== u.id));
  };

  const openThread = async (userId) => {
    setActiveThreadId(userId);
    const r = await axios.get(`${API}/admin/messages/${userId}`, { headers: authHeaders });
    setThreadMessages(r.data);
    setFamilyThreads(prev => prev.map(t => t.user_id === userId ? { ...t, unread_count: 0 } : t));
  };

  const sendReply = async () => {
    const body = replyDraft.trim();
    if (!body || !activeThreadId) return;
    setReplying(true);
    try {
      const r = await axios.post(`${API}/admin/messages/${activeThreadId}/reply`, { body }, { headers: authHeaders });
      setThreadMessages(prev => [...prev, r.data]);
      setReplyDraft('');
    } finally {
      setReplying(false);
    }
  };

  const filtered = users.filter(u => {
    if (freeAccessOnly && !u.free_access) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return u.email.toLowerCase().includes(q) || (u.family_name || '').toLowerCase().includes(q) ||
      (u.children || []).some(c => (c.name || '').toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center"><BrandMark size={36} /></Link>
          <div className="flex items-center gap-3">
            <Badge className="bg-fern-dark text-white">Admin</Badge>
            <Link to="/dashboard"><Button variant="ghost" className="gap-2"><ArrowLeft className="w-4 h-4" /> Dashboard</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-wide text-slate-400 font-semibold">Admin</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark">NestWise Admin</h1>
          <p className="text-slate-500 mt-1">Whānau, purchases, and contact messages — all in one place.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Whānau registered" value={stats.whanau_registered} />
          <StatCard icon={ShieldCheck} label="With free access" value={stats.with_free_access} />
          <StatCard icon={ShoppingBag} label="Active purchases" value={stats.active_purchases} />
          <StatCard icon={BarChart3} label="Revenue (NZD)" value={`$${(stats.revenue_cents / 100).toFixed(2)}`} />
        </div>

        <Card className="mb-8">
          <CardHeader>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Conversion funnel</p>
            <CardTitle className="text-xl">From sign-up to paying customer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.funnel.map(f => (
                <div key={f.stage} className="border border-slate-100 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Stage {f.stage}</p>
                  <p className="text-3xl font-bold text-fern-dark mt-1">{f.count}</p>
                  <p className="text-sm text-slate-500">{f.label}</p>
                  <p className="text-xs text-amber-600 mt-1">{f.pct_of_previous}% of previous · {f.pct_of_total}% of total</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="users">
          <TabsList className="mb-4">
            <TabsTrigger value="users" className="gap-2"><Users className="w-4 h-4" /> Users</TabsTrigger>
            <TabsTrigger value="purchases" className="gap-2"><ShoppingBag className="w-4 h-4" /> Purchases</TabsTrigger>
            <TabsTrigger value="messages">Contact Form</TabsTrigger>
            <TabsTrigger value="family-messages" className="gap-2">
              Family Messages
              {familyThreads.some(t => t.unread_count > 0) && (
                <span className="w-2 h-2 rounded-full bg-fern" data-testid="admin-unread-dot" />
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4 gap-4">
                  <Input placeholder="Search email, family or child name..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-sm" />
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Switch checked={freeAccessOnly} onCheckedChange={setFreeAccessOnly} /> Free access only
                  </label>
                </div>
                <p className="text-sm text-slate-400 mb-3">{users.length} whānau total · showing {filtered.length}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-4"></th>
                        <th className="py-2 pr-4">Whānau</th>
                        <th className="py-2 pr-4">Children</th>
                        <th className="py-2 pr-4">Joined</th>
                        <th className="py-2 pr-4">Trial</th>
                        <th className="py-2 pr-4">Maths</th>
                        <th className="py-2 pr-4">English</th>
                        <th className="py-2 pr-4">Purchases</th>
                        <th className="py-2 pr-4">Free access</th>
                        <th className="py-2 pr-4">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(u => {
                        const isExpanded = expandedUserId === u.id;
                        return (
                        <Fragment key={u.id}>
                        <tr className="border-b border-slate-50">
                          <td className="py-3 pl-1">
                            <button onClick={() => setExpandedUserId(isExpanded ? null : u.id)} className="text-slate-400 hover:text-slate-700">
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="font-medium text-slate-800 flex items-center gap-2">
                              {u.email}
                              {u.is_admin && <Badge className="bg-fern-dark text-white text-[10px]">Admin</Badge>}
                            </div>
                            <div className="text-slate-400 text-xs">{u.family_name}</div>
                          </td>
                          <td className="py-3 pr-4 text-slate-600">{(u.children || []).map(c => `${c.name} (Y${c.year_level ?? '?'})`).join(', ')}</td>
                          <td className="py-3 pr-4 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                          <td className="py-3 pr-4"><Badge variant="secondary">{u.trial_done} / {u.trial_total}</Badge></td>
                          <td className="py-3 pr-4">{u.maths_completed}</td>
                          <td className="py-3 pr-4">{u.english_completed}</td>
                          <td className="py-3 pr-4">{u.purchases}</td>
                          <td className="py-3 pr-4"><Switch checked={u.free_access} onCheckedChange={(v) => toggleFreeAccess(u, v)} /></td>
                          <td className="py-3 pr-4">
                            <button onClick={() => deleteUser(u)} className="text-slate-400 hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-slate-50/60 border-b border-slate-100">
                            <td colSpan={10} className="py-4 px-6">
                              <UserDetailBreakdown user={u} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="purchases">
            <Card>
              <CardContent className="pt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                      <th className="py-2 pr-4">Customer</th>
                      <th className="py-2 pr-4">Child</th>
                      <th className="py-2 pr-4">Subject</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Amount</th>
                      <th className="py-2 pr-4">Purchased</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map(p => (
                      <tr key={p.id} className="border-b border-slate-50">
                        <td className="py-3 pr-4">{p.customer_email}</td>
                        <td className="py-3 pr-4">{p.child_name}</td>
                        <td className="py-3 pr-4 capitalize">{p.subject}</td>
                        <td className="py-3 pr-4"><Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status}</Badge></td>
                        <td className="py-3 pr-4">${((p.amount_cents || 0) / 100).toFixed(2)} {p.currency}</td>
                        <td className="py-3 pr-4 text-slate-500">{p.purchased_at ? new Date(p.purchased_at).toLocaleDateString('en-NZ') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardContent className="pt-6 space-y-4">
                {messages.map(m => (
                  <div key={m.id} className="border border-slate-100 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-800">{m.name} &lt;{m.email}&gt;</p>
                      <p className="text-xs text-slate-400">{m.created_at ? new Date(m.created_at).toLocaleString('en-NZ') : ''}</p>
                    </div>
                    <p className="text-sm font-medium text-slate-600 mt-1">{m.subject}</p>
                    <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-sm text-slate-400">No messages yet.</p>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="family-messages">
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1 border-r border-slate-100 pr-4 space-y-2 max-h-[500px] overflow-y-auto">
                    {familyThreads.length === 0 && <p className="text-sm text-slate-400">No family messages yet.</p>}
                    {familyThreads.map(t => (
                      <button
                        key={t.user_id}
                        data-testid={`thread-${t.user_id}`}
                        onClick={() => openThread(t.user_id)}
                        className={`w-full text-left p-3 rounded-xl border ${
                          activeThreadId === t.user_id ? 'border-fern bg-fern/5' : 'border-slate-100 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-800 text-sm">{t.family_name || t.email}</p>
                          {t.unread_count > 0 && (
                            <Badge className="bg-fern text-white border-0 rounded-full text-[10px]">{t.unread_count}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          {t.last_sender === 'admin' ? 'You: ' : ''}{t.last_message}
                        </p>
                      </button>
                    ))}
                  </div>
                  <div className="md:col-span-2">
                    {!activeThreadId ? (
                      <p className="text-sm text-slate-400">Select a family to view the conversation.</p>
                    ) : (
                      <>
                        <div className="space-y-3 max-h-[380px] overflow-y-auto mb-3 pr-2">
                          {threadMessages.map(m => (
                            <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`rounded-2xl px-4 py-2 max-w-[80%] text-sm ${
                                m.sender === 'admin' ? 'bg-fern text-white' : 'bg-slate-100 text-slate-700'
                              }`}>
                                {m.body}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Textarea
                            data-testid="admin-reply-input"
                            value={replyDraft}
                            onChange={e => setReplyDraft(e.target.value)}
                            placeholder="Type a reply…"
                            className="min-h-[44px] resize-none"
                          />
                          <Button
                            data-testid="admin-reply-send"
                            onClick={sendReply}
                            disabled={replying || !replyDraft.trim()}
                            className="bg-fern hover:bg-fern-light text-white rounded-full shrink-0 self-end"
                          >
                            Send
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function UserDetailBreakdown({ user }) {
  const children = user.children_detail || [];
  if (children.length === 0) return <p className="text-sm text-slate-400">No children on this account.</p>;

  return (
    <div className="space-y-4">
      {children.map(c => (
        <div key={c.name || 'unnamed'} className="border border-slate-200 rounded-lg bg-white p-3">
          <p className="font-medium text-slate-800 mb-2">{c.name || '(unnamed child)'}{c.year_level ? ` — Y${c.year_level}` : ''}</p>

          {Object.keys(c.years).length > 0 && (
            <table className="w-full text-xs mb-3">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wide">
                  <th className="py-1 pr-4">Year</th>
                  <th className="py-1 pr-4">Trial</th>
                  <th className="py-1 pr-4">Maths done</th>
                  <th className="py-1 pr-4">English done</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(c.years).sort(([a], [b]) => a - b).map(([year, y]) => (
                  <tr key={year} className="border-t border-slate-50">
                    <td className="py-1 pr-4">Y{year}</td>
                    <td className="py-1 pr-4">{y.trial_done}</td>
                    <td className="py-1 pr-4">{y.maths_completed}</td>
                    <td className="py-1 pr-4">{y.english_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {c.purchases.length === 0 ? (
            <p className="text-xs text-slate-400">No purchases for this child.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wide">
                  <th className="py-1 pr-4">Subject</th>
                  <th className="py-1 pr-4">Plan</th>
                  <th className="py-1 pr-4">Year</th>
                  <th className="py-1 pr-4">Term/Week</th>
                  <th className="py-1 pr-4">Status</th>
                  <th className="py-1 pr-4">Amount</th>
                  <th className="py-1 pr-4">Purchased</th>
                </tr>
              </thead>
              <tbody>
                {c.purchases.map((p, i) => (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="py-1 pr-4 capitalize">{p.subject}</td>
                    <td className="py-1 pr-4 capitalize">{p.plan}</td>
                    <td className="py-1 pr-4">Y{p.year_level}</td>
                    <td className="py-1 pr-4">{p.term ? `T${p.term} W${p.week}` : '—'}</td>
                    <td className="py-1 pr-4">
                      <Badge variant={p.status === 'active' ? 'secondary' : 'outline'} className="text-[10px]">{p.status}</Badge>
                    </td>
                    <td className="py-1 pr-4">${(p.amount_cents / 100).toFixed(2)}</td>
                    <td className="py-1 pr-4 text-slate-500">{p.purchased_at ? new Date(p.purchased_at).toLocaleDateString('en-NZ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
          <Icon className="w-4 h-4 text-slate-600" />
        </div>
        <p className="text-3xl font-bold text-fern-dark">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </CardContent>
    </Card>
  );
}
